import { useState } from 'react'
import { useLocation } from 'react-router-dom'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { AccountsPayableUpsellModalDocument } from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { UpsellSourceType } from '../lib/upsellUtils'

const TARGET_COUNTRIES = ['ES', 'FR', 'IT', 'PT']

export const useAccountsPayableUpsellModal = (source: UpsellSourceType) => {
  const location = useLocation()
  const [showAccountsUpsellModal, setShowAccountsUpsellModal] = useState<UpsellSourceType | null>(
    null
  )
  const { data } = useQuery(AccountsPayableUpsellModalDocument)
  const [hasBeenSeenBefore, setHasBeenSeenBefore] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.hideFinanceAccountsPayableUpsellModal,
    false
  )

  const hasALegalEntityFromTargetCountries = useUnsafeMemo(
    () =>
      (data?.companies?.legalEntities ?? []).some((le) =>
        TARGET_COUNTRIES.includes(le.country.toUpperCase())
      ),
    [data]
  )

  const hasAccountsPayable = useUnsafeMemo(
    () => data?.companies?.featureAvailability?.available ?? true, // Default to true, we don't want to show the modal if we don't have the data
    [data]
  )

  useUnsafeEffect(() => {
    if (!hasAccountsPayable && hasALegalEntityFromTargetCountries && !hasBeenSeenBefore) {
      setShowAccountsUpsellModal(source)
    }
  }, [location, hasALegalEntityFromTargetCountries, hasAccountsPayable, source, hasBeenSeenBefore])

  const onClose = useUnsafeCallback(() => {
    setHasBeenSeenBefore(true)
    setShowAccountsUpsellModal(null)
  }, [setHasBeenSeenBefore])

  return {
    showAccountsUpsellModal,
    onClose,
  }
}
